/**
 * Shared fim-sentinel contract: the entry shape and the range-scoring
 * response, used by both the Dossiers sentinel views (pages/modelOperations/sentinels)
 * and the Process Data chart overlay (features/timeSeries). Design/run/result
 * types stay in features/sentinels/recordTypes.
 */

export const SENTINEL_ENTRY_TYPE = 'fim-sentinel'

/**
 * `building` is a design-context entry uploading its bundle: the work is over
 * but nothing on the entry is readable yet, so it is still in flight.
 */
export type SentinelRunStatus = 'running' | 'building' | 'done' | 'error'
/**
 * Only `target` and `multitag` are sentinels. The rest share the `fim-sentinel`
 * entry type for backend convenience and are never listed as sentinels:
 * `preview` marks ephemeral job-handoff scratch entries, `experiment` an
 * Experiment Lab save, and `design_context` the context bundle a design run is
 * planned from. `isListableSentinel` is the one place that decides.
 */
export type SentinelMode =
  | 'target'
  | 'multitag'
  | 'experiment'
  | 'preview'
  | 'design_context'
  | 'case'

/**
 * The last progress frame the training job mirrored onto the entry. fim-service's
 * SSE relay forwards exactly this field, so it is what a page that lost its
 * stream reads instead of re-attaching. Only the LATEST frame is kept — there is
 * no persisted scrollback — and the job writes a whitelist of keys, so step
 * counters and loss curves never appear here.
 */
export type SentinelProgress = {
  type?: string
  stage?: string
  message?: string
  method?: string
  epoch?: number
  max_iter?: number
  rows?: number
  tags?: number
  /** Stamped on every mirror, so it doubles as the job's heartbeat. */
  updated_at?: string
}

/**
 * Why a run failed, in more than the one line `summary.error` has room for.
 *
 * A run that ends with an empty frame is the common failure and the least
 * self-explanatory one: the same "no rows" outcome comes from a historian that
 * returned nothing, from cleaning gates that dropped everything, and from a fit
 * that threw. Which of those it was decides what the user changes next, so the
 * job records the stage and the row counts it saw at each step rather than
 * leaving the detail view to guess from a sentence.
 */
export interface SentinelRunFailure {
  /** `fit` or `refit` — a run fits twice, and they fail for different reasons. */
  phase?: string
  /** The job's own name for the step: `pull`, `clean` or `fit`. */
  stage?: string
  error?: string
  /** Row counts through the pull → clean → gate → align pipeline. */
  funnel?: SentinelRunFunnel
}

export interface SentinelRunFunnel {
  sampling_mode?: string
  interval?: number
  interval_unit?: string
  raw_grid_rows?: number
  /** Observed (non-null) values per tag in the raw pull, keyed by tag id. */
  raw_observed_per_tag?: Record<string, number>
  after_cleaning_rows?: number
  after_gate_rows?: number
  aligned_grid_rows?: number
  target_observed_rows?: number
}

export const TARGET_METHOD_KEYS = [
  'target_mlp_none',
  'target_mlp_driver',
  'target_mlp_driver_own',
  'target_knn',
] as const

export type SentinelTargetMethodKey = (typeof TARGET_METHOD_KEYS)[number]

/**
 * The recurring-monitor config, seeded onto a sentinel at fit time and edited in
 * the Monitor tab. Disabled by default — nothing runs until it is turned on.
 * The band is a multiplier on the served method's residual scale: a breach is
 * `|residual| > band_k * err_scale`, which is how the model's band is defined.
 */
export interface SentinelMonitorConfig {
  enabled: boolean
  cadence_minutes: number
  /** The target datapoint id being predicted (fixed; matches the plan). */
  target?: string | null
  /** Which fitted method's residuals the monitor watches. */
  method?: string | null
  band_k: number
  /** ISO time of the last monitor tick that scored this sentinel. */
  last_run_at?: string | null
}

export interface SentinelEntryData {
  name: string
  run_id: string
  summary?: {
    status?: SentinelRunStatus
    mode?: SentinelMode
    error?: string
    /** The structured half of `error`, when the failure had stages to name. */
    failure?: SentinelRunFailure
    progress?: SentinelProgress
    requested_at?: string
    target?: string
    drivers?: number
    tags?: number
    families?: number
    start_date?: string
    end_date?: string
    interval_minutes?: number
    rows?: number
    split_date?: string
    /** The design-context entry this run was planned from, on run entries. */
    context_entry_id?: string
    has_evaluation?: boolean
    evaluation_overall?: string
    /** Context entries only: set once the design agent has written a plan. */
    has_plan?: boolean
    /** Counts redesigns of the same context, so a cached plan can be dropped. */
    plan_revision?: number
    plan_designed_at?: string
    plan_target?: string
    plan_drivers?: number
    eligible?: boolean
  }
  /**
   * Entry-relative paths of the entry's attached files. A run entry gains
   * `plan`/`context`/`result` only when the job finishes; the context entry it
   * was planned from carries `plan` from the moment the plan is designed, which
   * is what makes an in-flight run's plan readable at all.
   */
  files?: {
    request?: string
    result?: string
    models?: string
    plan?: string
    design?: string
    context?: string
    evaluation?: string
    diagnostics?: string
  }
  /** Recurring-monitor config, seeded at fit time; edited in the Monitor tab. */
  monitor?: SentinelMonitorConfig
}

export interface SentinelLoadedEntry {
  entry: {
    id: string
    type: string
    data: SentinelEntryData
    tags: Record<string, string>
    updated_at: string
  }
}

/**
 * Longest scorable range; matches the service-side guard. Raised well past the
 * original 14 days so the Process Data chart can render a sentinel's full
 * multi-year trained window plus forward scoring in one pass (the service pulls
 * the historian in parallel 14-day chunks, so a multi-year window is still tens
 * of seconds).
 */
export const MAX_SCORE_DAYS = 1500

/** One method's output of range scoring (native-interval series). */
export interface ScoreRecentMethod {
  band_k: number
  expected: Record<string, (number | null)[]>
  residual: Record<string, (number | null)[]>
  err_scale: Record<string, number | null>
  residual_band: Record<string, number | null>
}

/** Result of scoring a date range with the sentinel's saved models. */
export interface ScoreRecentResponse {
  ok: boolean
  error?: string
  entry_id?: string
  mode?: SentinelMode
  target?: string
  drivers?: string[]
  window?: {
    start: string
    end: string
    interval_minutes: number
  }
  trained_split_date?: string
  model_created_at?: string
  ts?: string[]
  tags?: string[]
  actual?: Record<string, (number | null)[]>
  methods?: Record<string, ScoreRecentMethod>
}
